import { useEffect, useState } from "react";
import { Button, Chip, Field, SegmentedControl, Sheet } from "../../../design-v2";
import * as defaultApi from "../api/coachApi";
import { getWorkoutLogPreferences } from "../../workoutLog/api/workoutLogApi";
import { countChars, MAX_CHECKIN_NOTE_CHARS, todayKey } from "../domain/coachFormat";
import { CHECKIN_SCALES, CONSTRAINT_TYPE_OPTIONS, PAIN_AREA_OPTIONS } from "../domain/coachVocabulary";

/*
 * Weekly check-in (blueprint §12.6, contract §5.4): seven answers, every one
 * optional, sent in one tap. No model call. Body weight uses the athlete's
 * preferred unit when Workout Preferences has one.
 */

const WEIGHT_RANGES = { KG: [20, 400], LB: [44, 880] };
const EMPTY = {
  energy: null,
  sleep: null,
  stress: null,
  pain: null,
  painArea: null,
  workoutName: "",
  constraint: null,
  constraintType: null,
  constraintDate: "",
  weight: "",
  unit: "KG",
  note: "",
};

// Form values → the POST body. Returns { body } or { error }.
export function buildCheckinBody(values) {
  let bodyWeight = null;
  const weightText = String(values.weight || "").trim().replace(",", ".");
  if (weightText) {
    const value = Math.round(Number(weightText) * 10) / 10;
    const [min, max] = WEIGHT_RANGES[values.unit] || WEIGHT_RANGES.KG;
    if (!Number.isFinite(value) || value < min || value > max) {
      return { error: { field: "weight", message: `Enter a weight between ${min} and ${max} ${values.unit === "LB" ? "lb" : "kg"}.` } };
    }
    bodyWeight = { value, unit: values.unit === "LB" ? "LB" : "KG" };
  }
  const note = String(values.note || "").trim();
  if (countChars(note) > MAX_CHECKIN_NOTE_CHARS) {
    return { error: { field: "note", message: `Keep it under ${MAX_CHECKIN_NOTE_CHARS} characters.` } };
  }
  return {
    body: {
      energy: values.energy,
      sleep: values.sleep,
      stress: values.stress,
      pain:
        values.pain === "YES"
          ? { present: true, area: values.painArea || null, workoutName: values.workoutName.trim().slice(0, 60) || null }
          : values.pain === "NO"
            ? { present: false }
            : null,
      upcomingConstraint:
        values.constraint === "YES"
          ? { present: true, type: values.constraintType || "OTHER", date: values.constraintDate || null }
          : values.constraint === "NO"
            ? { present: false }
            : null,
      bodyWeight,
      note: note || null,
    },
  };
}

// A chip row where tapping the selected answer clears it (every field is optional).
function ChoiceRow({ label, options, value, onChange }) {
  return (
    <div className="lz-coach-checkin-field" role="group" aria-label={label}>
      <span className="lz-coach-checkin-field__label">{label}</span>
      <div className="lz-coach-chip-group">
        {options.map(([optionValue, optionLabel]) => (
          <Chip key={optionValue} selected={value === optionValue} onClick={() => onChange(value === optionValue ? null : optionValue)}>
            {optionLabel}
          </Chip>
        ))}
      </div>
    </div>
  );
}

const YES_NO = [["NO", "No"], ["YES", "Yes"]];

export default function CheckinSheet({ open, onClose, checkin, onSubmitted, onHomeStale, api = defaultApi, loadPreferences = getWorkoutLogPreferences }) {
  const [values, setValues] = useState(EMPTY);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState("idle");
  const update = (patch) => setValues((current) => ({ ...current, ...patch }));

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setValues(EMPTY);
    setError(null);
    setStatus("idle");
    Promise.resolve()
      .then(() => loadPreferences())
      .then((preferences) => {
        if (active && preferences?.weightUnit === "LB") update({ unit: "LB" });
      })
      .catch(() => {
        // kg stays the default
      });
    return () => {
      active = false;
    };
  }, [open, loadPreferences]);

  const done = checkin && checkin.due === false;

  async function submit(event) {
    event.preventDefault();
    const result = buildCheckinBody(values);
    if (result.error) {
      setError(result.error);
      return;
    }
    setStatus("sending");
    setError(null);
    try {
      const response = await api.postCoachCheckin(result.body);
      onSubmitted?.(response);
      setStatus("sent");
    } catch (failure) {
      if (failure?.code === "COACH_CHECKIN_ALREADY_SUBMITTED") {
        onSubmitted?.({ event: null, alreadySubmittedEventId: failure.details?.eventId || null });
        setStatus("already");
        return;
      }
      if (["COACH_CONSENT_REQUIRED", "COACH_NOT_AVAILABLE", "COACH_UNAVAILABLE"].includes(failure?.code)) onHomeStale?.();
      setStatus("idle");
      setError({ field: null, message: failure?.code === "VALIDATION_ERROR" ? "Some answers can't be saved. Check them and try again." : "Couldn't send your check-in. Try again." });
    }
  }

  if (open && (done || status === "already") && status !== "sent") {
    return (
      <Sheet open={open} onClose={onClose} title="Weekly check-in" surfaceClassName="lz-coach-sheet">
        <p className="lz-coach-sheet__empty">You've already checked in this week. The next one opens on Monday.</p>
        <Button variant="secondary" className="lz-coach-sheet__wide" onClick={onClose}>
          Close
        </Button>
      </Sheet>
    );
  }

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Weekly check-in"
      description="A minute to tell your coach how the week went. Skip anything you like."
      surfaceClassName="lz-coach-sheet"
    >
      <form className="lz-coach-checkin" onSubmit={submit}>
        {Object.entries(CHECKIN_SCALES).map(([field, scale]) => (
          <ChoiceRow key={field} label={scale.label} options={scale.options} value={values[field]} onChange={(value) => update({ [field]: value })} />
        ))}

        <ChoiceRow label="Pain or discomfort" options={YES_NO} value={values.pain} onChange={(pain) => update({ pain })} />
        {values.pain === "YES" ? (
          <div className="lz-coach-checkin-detail">
            <div className="lz-coach-chip-group" role="group" aria-label="Where">
              {PAIN_AREA_OPTIONS.map((option) => (
                <Chip
                  key={option.value}
                  selected={values.painArea === option.value}
                  onClick={() => update({ painArea: values.painArea === option.value ? null : option.value })}
                >
                  {option.label}
                </Chip>
              ))}
            </div>
            <Field label="During which workout?">
              <input
                className="lz-coach-input"
                value={values.workoutName}
                maxLength={60}
                onChange={(event) => update({ workoutName: event.target.value })}
                placeholder="Legs A"
              />
            </Field>
            <p className="lz-coach-checkin-detail__hint">If it keeps coming back, report it in Pain issues so your training adapts.</p>
          </div>
        ) : null}

        <ChoiceRow label="Something big coming up" options={YES_NO} value={values.constraint} onChange={(constraint) => update({ constraint })} />
        {values.constraint === "YES" ? (
          <div className="lz-coach-checkin-detail">
            <div className="lz-coach-chip-group" role="group" aria-label="What">
              {CONSTRAINT_TYPE_OPTIONS.map((option) => (
                <Chip
                  key={option.value}
                  selected={values.constraintType === option.value}
                  onClick={() => update({ constraintType: values.constraintType === option.value ? null : option.value })}
                >
                  {option.label}
                </Chip>
              ))}
            </div>
            <Field label="When?">
              <input
                type="date"
                className="lz-coach-input"
                value={values.constraintDate}
                min={todayKey()}
                onChange={(event) => update({ constraintDate: event.target.value })}
              />
            </Field>
          </div>
        ) : null}

        <div className="lz-coach-checkin-weight">
          <Field label="Body weight" error={error?.field === "weight" ? error.message : null}>
            <input
              className="lz-coach-input"
              inputMode="decimal"
              value={values.weight}
              onChange={(event) => update({ weight: event.target.value })}
              placeholder={values.unit === "LB" ? "180.5" : "81.4"}
            />
          </Field>
          <SegmentedControl
            label="Weight unit"
            options={[{ value: "KG", label: "kg" }, { value: "LB", label: "lb" }]}
            value={values.unit}
            onChange={(unit) => update({ unit })}
          />
        </div>

        <Field
          label="Anything else?"
          hint={`${countChars(values.note)} / ${MAX_CHECKIN_NOTE_CHARS}`}
          error={error?.field === "note" ? error.message : null}
        >
          <textarea className="lz-coach-textarea" rows={2} value={values.note} onChange={(event) => update({ note: event.target.value })} />
        </Field>

        {error && !error.field ? <p className="lz-coach-inline-error" role="alert">{error.message}</p> : null}
        <Button type="submit" size="lg" className="lz-coach-sheet__wide" isLoading={status === "sending"} loadingLabel="Sending">
          Send check-in
        </Button>
      </form>
    </Sheet>
  );
}
